import type { CampaignScheduleDto } from '@carbonwebmail/contracts'
import { problemsWithSchedule } from '@carbonwebmail/domain'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Field, SelectField } from '../../ui/Field.js'
import { count } from '../../ui/format.js'

/**
 * Choosing when a campaign goes out, and in whose clock.
 *
 * **The strategy is a choice somebody makes on purpose, not a checkbox.** The
 * two options mean genuinely different things — one instant for everybody, or
 * the same reading on each person's own clock — and the second one spreads a
 * send across more than a day. Somebody who expects a campaign to be finished
 * by lunchtime should find that out here, while they can still choose
 * otherwise, which is why the panel says when it will have reached everybody.
 *
 * **The validation is the domain's, run twice on purpose.** `problemsWithSchedule`
 * is called here so the button can refuse before a request, and again on the
 * server because a disabled button is not a control. Both calls are the same
 * function, so the sentence somebody reads is the sentence the server decided.
 */

/** Zones offered by name. Anything typed is checked against the platform's own list. */
const COMMON_ZONES: readonly (readonly [string, string])[] = [
  ['UTC', 'UTC'],
  ['Europe/London', 'London'],
  ['Europe/Berlin', 'Berlin, Paris, Madrid'],
  ['America/New_York', 'New York'],
  ['America/Chicago', 'Chicago'],
  ['America/Los_Angeles', 'Los Angeles'],
  ['Asia/Karachi', 'Karachi'],
  ['Asia/Dubai', 'Dubai'],
  ['Asia/Kolkata', 'Kolkata'],
  ['Asia/Singapore', 'Singapore'],
  ['Asia/Tokyo', 'Tokyo'],
  ['Australia/Sydney', 'Sydney'],
]

export interface ScheduleOutcome {
  readonly finishedBy: string
  readonly usingFallback: number
  readonly audience: number
}

export function SchedulePanel({
  busy,
  scheduled,
  outcome,
  now,
  onSchedule,
  onCancel,
}: {
  readonly busy: boolean
  /** What is already set, so a scheduled campaign shows its own moment. */
  readonly scheduled: CampaignScheduleDto | undefined
  /** What the server answered last time, which carries the counts. */
  readonly outcome: ScheduleOutcome | undefined
  readonly now: Date
  readonly onSchedule: (schedule: CampaignScheduleDto) => void
  readonly onCancel: () => void
}) {
  const [open, setOpen] = useState(scheduled !== undefined)
  const [kind, setKind] = useState<CampaignScheduleDto['kind']>(scheduled?.kind ?? 'at-once')
  const [at, setAt] = useState(scheduled?.kind === 'at-once' ? localInput(scheduled.at) : '')
  const [localAt, setLocalAt] = useState(
    scheduled?.kind === 'recipient-local' ? scheduled.localAt : '',
  )
  const [fallback, setFallback] = useState(
    scheduled?.kind === 'recipient-local' ? scheduled.fallbackTimezone : 'UTC',
  )

  const typed = at === '' && localAt === ''
  const wanted = build(kind, at, localAt, fallback)
  // Nothing is wrong with a form nobody has filled in yet, and saying so would
  // put an error beside a field somebody is about to type into.
  const problems = typed || wanted === undefined ? [] : problemsWithSchedule(toModel(wanted), now)

  if (!open) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen(true)}
        className="rounded-pill border border-line px-5 py-2.5 font-medium text-[14px] text-t2 hover:text-t1 disabled:opacity-60"
      >
        Schedule it instead
      </button>
    )
  }

  return (
    <section className="mt-5 w-full rounded-card border border-line bg-surface p-5">
      <h3 className="font-bold text-[15px] text-t1">When should it go out?</h3>

      <SelectField
        id="schedule-kind"
        label="Delivery"
        value={kind}
        disabled={busy}
        options={[
          ['at-once', 'At one moment, for everybody'],
          ['recipient-local', 'At a local time, in each recipient’s own timezone'],
        ]}
        hint={
          kind === 'at-once'
            ? 'Everyone receives it at the same instant, wherever they are.'
            : 'Nine in the morning means nine in the morning for each person. The send spreads across a day and a bit.'
        }
        onChange={(value) => setKind(value as CampaignScheduleDto['kind'])}
      />

      {kind === 'at-once' ? (
        <Field
          id="schedule-at"
          label="Date and time"
          type="datetime-local"
          autoComplete="off"
          value={at}
          disabled={busy}
          hint={`In your browser’s timezone — ${browserZone()}.`}
          onChange={setAt}
        />
      ) : (
        <>
          <Field
            id="schedule-local-at"
            label="Local date and time"
            type="datetime-local"
            autoComplete="off"
            value={localAt}
            disabled={busy}
            hint="The reading on each recipient’s own clock. No timezone is attached to it."
            onChange={setLocalAt}
          />
          <SelectField
            id="schedule-fallback"
            label="For contacts with no timezone recorded"
            value={fallback}
            disabled={busy}
            options={COMMON_ZONES}
            hint="Most imported lists have none. This is the clock they will be treated as keeping."
            onChange={setFallback}
          />
        </>
      )}

      {problems.length === 0 ? null : (
        <Alert>
          <ul>
            {problems.map((one) => (
              <li key={one}>{one}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/*
        Said after it is set rather than predicted before, because the counts
        come from the audience as the server resolved it — the same expression
        the queue insert will use — and a number this screen worked out for
        itself could disagree with the one that actually sends.
      */}
      {outcome === undefined ? null : (
        <Alert tone="ok">
          Scheduled for {count(outcome.audience)} {outcome.audience === 1 ? 'person' : 'people'}.{' '}
          {kind === 'recipient-local' ? (
            <>
              It will have reached everybody by {whenText(outcome.finishedBy)}.{' '}
              {outcome.usingFallback === 0
                ? 'Everyone has a timezone recorded.'
                : `${count(outcome.usingFallback)} of them have no timezone recorded and will be treated as being in ${fallback}.`}
            </>
          ) : (
            <>Everyone receives it at {whenText(outcome.finishedBy)}.</>
          )}
        </Alert>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3 border-line border-t pt-5">
        <button
          type="button"
          disabled={busy || wanted === undefined || problems.length > 0}
          onClick={() => wanted !== undefined && onSchedule(wanted)}
          className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? 'Working…' : scheduled === undefined ? 'Schedule it' : 'Change the time'}
        </button>

        {scheduled === undefined ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => setOpen(false)}
            className="font-medium text-[13px] text-t3 hover:text-t1"
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="font-medium text-[13px] text-danger hover:underline"
          >
            Unschedule, and keep it as a draft
          </button>
        )}
      </div>
    </section>
  )
}

/**
 * The two inputs, as the one shape the contract takes.
 *
 * `undefined` for a form that is not filled in yet rather than a half-built
 * object, so the button has one thing to test and cannot send a schedule with
 * an empty date in it.
 */
function build(
  kind: CampaignScheduleDto['kind'],
  at: string,
  localAt: string,
  fallbackTimezone: string,
): CampaignScheduleDto | undefined {
  if (kind === 'at-once') {
    if (at === '') return undefined
    // `datetime-local` has no zone, and the browser's own is the one the person
    // reading the field is thinking in. `Date` parses it that way, and the
    // contract wants UTC.
    const parsed = new Date(at)
    return Number.isNaN(parsed.getTime()) ? undefined : { kind, at: parsed.toISOString() }
  }

  // Seconds are not part of the wall clock the contract accepts, and some
  // browsers add them.
  return localAt === '' ? undefined : { kind, localAt: localAt.slice(0, 16), fallbackTimezone }
}

/** The contract's strings, as the domain's model, so one validator serves both. */
function toModel(dto: CampaignScheduleDto) {
  return dto.kind === 'at-once' ? { kind: 'at-once' as const, at: new Date(dto.at) } : dto
}

/** An instant, as a `datetime-local` input wants it: local, and without a zone. */
function localInput(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const shifted = new Date(at.getTime() - at.getTimezoneOffset() * 60_000)
  return shifted.toISOString().slice(0, 16)
}

function whenText(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** Named so the field can say which clock it is asking in. */
function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'your local time'
  }
}
